import React, { FC } from 'react';
import './Result.scss';
import { IResultProps } from './IResultProps';
import { IGlobalResult } from '../../../../Models/IResult';
import { getLanguage } from '../../../../utils/searchHelpers';
import { Card, Paragraph, Heading, Tag } from '@udir-design/react/beta';
import { getContentType, getLastModified, getTeaserText, getIntroTitleHtml, getFoRTeaser } from '../../../../utils/resultHelpers';
import { DisplayFormat } from '../../../../Models/DisplayFormat';
import { getCurrentDisplayFormat } from '../../../../utils/generalHelpers';
import LaereplanResultCard from '../../../SharedComponents/ResultCard/LaereplanResultCard';
import EksamensResultCard from '../../../SharedComponents/ResultCard/EksamensResultCard';

// Check if result is an eksamen type
const isEksamensoppgaveResult = (result: IGlobalResult): boolean => {
    return result.ContentSource === "Doklager";
};

// Check if result is a Forskning og Rapporter type
const isForskningOgRapporterResult = (result: IGlobalResult): boolean => {
    // FoR results have rapportutgiver or rapportnivaa
    if (result.rapportutgiver && result.rapportutgiver.trim().length > 0) {
        return true;
    }
    if (result.rapportnivaa && result.rapportnivaa.trim().length > 0) {
        return true;
    }
    return false;
};

// Check if result is a Regelverkstolkninger type
const isRegelverkResult = (result: IGlobalResult): boolean => {
    // Regelverk results have regelverkslov
    if (result.regelverkslov && result.regelverkslov.trim().length > 0) {
        return true;
    }
    // Or check by innholdstype
    const regelverkTypes = ['Regelverkstolkning', 'Paragrafer i forskrift', 'Paragrafer i lov', 'Lov- og forskrift'];
    if (result.innholdstype && regelverkTypes.includes(result.innholdstype)) {
        return true;
    }
    return false;
};

// Check if result is a læreplan type
const isLaereplanResult = (result: IGlobalResult): boolean => {
    // Eksamensoppgaver should not be treated as læreplan
    if (isEksamensoppgaveResult(result)) {
        return false;
    }
    // FoR should not be treated as læreplan
    if (isForskningOgRapporterResult(result)) {
        return false;
    }
    // Regelverk should not be treated as læreplan
    if (isRegelverkResult(result)) {
        return false;
    }
    const laereplanTypes = ['Læreplan', 'Fagkode', 'Elementer i læreplanen LK20', 'Overordnet del kapittel', 'Overordnet del', 'Støtteressurs'];
    if (result.fltypefilter && laereplanTypes.includes(result.fltypefilter)) {
        return true;
    }
    // Fallback: check URL for lk20/kl06 patterns
    if (result.Url && (result.Url.indexOf('/lk20/') > -1 || result.Url.indexOf('/kl06/') > -1)) {
        return true;
    }
    return false;
};

const Result: FC<IResultProps> = (props) => {
    let { result } = props;
    let language_attr = getLanguage(result);
    const teaser = getTeaserText(result);
    const isMobile = getCurrentDisplayFormat() === DisplayFormat.Mobile;
    
    // Use eksamensoppgave-specific styling for eksamen results
    if (isEksamensoppgaveResult(result)) {
        return <EksamensResultCard result={result} idx={props.idx} />;
    }
    
    // Use Forskning og Rapporter-specific styling
    if (isForskningOgRapporterResult(result)) {
        const displayTitle = result.udirtitle || result.title;
        const introTitle = getIntroTitleHtml(result);
        const dateFormatted = getLastModified(result);
        const nivaa = result.rapportnivaa ? result.rapportnivaa.replaceAll(";", ", ") : "";
        const hasNivaa = nivaa.trim().length > 0;
        const utgiver = result.rapportutgiver ? result.rapportutgiver.replaceAll(";", ", ") : "";
        const hasUtgiver = utgiver.trim().length > 0;
        
        return (
            <Card className="ResultLinkCard" key={`resultDisplay-${props.idx}`}>
                <Card.Block className="LinkCard__container">
                    <div className="LinkCard">
                        <Paragraph className="LinkCard__headerandsubtitle" data-size="sm">
                            <span className="subtitle-top title">{getContentType(result)}</span>
                            {introTitle && (
                                <span className="subtitle-top introtitle">{introTitle}</span>
                            )}
                            {dateFormatted && (
                                <span className="subtitle-top date">{dateFormatted}</span>
                            )}
                        </Paragraph>
                        
                        <Heading level={2} className="headerandbuttons" {...language_attr}>
                            <a className="cardtitle" href={result.Url} rel="noopener noreferrer">
                                {displayTitle}
                            </a>
                        </Heading>

                        {getFoRTeaser(result)}

                        {(hasNivaa || hasUtgiver) && (
                            <div className="TagGroup metadata" data-size={isMobile ? 'sm' : 'md'}>
                                {hasNivaa && (
                                    <Tag data-color="accent">{nivaa}</Tag>
                                )}
                                {hasUtgiver && (
                                    <Tag data-color="accent">{utgiver}</Tag>
                                )}
                            </div>
                        )}
                    </div>    
                </Card.Block>
            </Card>
        );
    }
    
    // Use Regelverkstolkninger-specific styling
    if (isRegelverkResult(result)) {
        const displayTitle = result.udirtitle || result.title;
        const introTitle = getIntroTitleHtml(result);
        const shouldShowDate = !!result.datoendret
            && !!result.innholdstype
            && result.innholdstype !== "Tema"
            && result.innholdstype !== "Paragrafer i forskrift"
            && result.innholdstype !== "Paragrafer i lov";
        const dateFormatted = shouldShowDate ? getLastModified(result) : undefined;
        const hasRegelverkslov = result.regelverkslov && result.regelverkslov.trim().length > 0;
        const isUtgatt = result.gyldighet === "Utgått innhold";
        
        return (
            <Card className="ResultLinkCard" key={`resultDisplay-${props.idx}`}>
                <Card.Block className="LinkCard__container">
                    <div className="LinkCard">
                        <Paragraph className="LinkCard__headerandsubtitle" data-size="sm">
                            <span className="subtitle-top title">{getContentType(result)}</span>
                            {introTitle && (
                                <span className="subtitle-top introtitle">{introTitle}</span>
                            )}
                            {dateFormatted && (
                                <span className="subtitle-top date">{dateFormatted}</span>
                            )}
                        </Paragraph>
                        
                        <Heading level={2} className="headerandbuttons" {...language_attr}>
                            <a className="cardtitle" href={result.Url} rel="noopener noreferrer">
                                {displayTitle}
                            </a>
                        </Heading>

                        {teaser && <Paragraph dangerouslySetInnerHTML={teaser} className="teaser" />}

                        {(hasRegelverkslov || isUtgatt) && (
                            <div className="TagGroup metadata" data-size={isMobile ? 'sm' : 'md'}>
                                {hasRegelverkslov && (
                                    <Tag data-color="accent">{result.regelverkslov}</Tag>
                                )}
                                {isUtgatt && (
                                    <Tag data-color="warning">Utgått</Tag>
                                )}
                            </div>
                        )}
                    </div>    
                </Card.Block>
            </Card>
        );
    }
    
    // Use læreplan-specific styling for læreplan results
    if (isLaereplanResult(result)) {
        return <LaereplanResultCard result={result} idx={props.idx} />;
    }
    
    // Default styling for non-læreplan results
    const displayTitle = result.udirtitle ? result.udirtitle : result.title;
    return (
        <Card  className="ResultPaneCard" key={`resultDisplay-${props.idx}`}>
            
            <Card.Block>
                <Paragraph data-size="sm">
                    <span className="title">{getContentType(result)}</span>
                    <span className="date">{getLastModified(result)}</span>
                </Paragraph>
                
                <Heading level={2}>
                    <a href={result.Url} rel="noopener noreferrer">
                        {displayTitle}
                    </a>
                </Heading>
                
                {teaser && <Paragraph dangerouslySetInnerHTML={teaser} className="teaser" />}
                
            </Card.Block>
            
            
        </Card>
    )
}

export default Result;
